<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Sourcing Events</h5>
        <p class="page-sub">Manage RFQs, RFPs and other sourcing activities</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" style="font-size:10px" />
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="doExport('csv')"><font-awesome-icon icon="file-csv" class="me-2" />Export CSV</button>
            <button class="export-opt" @click="doExport('pdf')"><font-awesome-icon icon="file-pdf" class="me-2" />Export PDF</button>
          </div>
        </div>
        <button class="btn-primary" @click="$router.push('/procurement/sourcing-events/create')">
          <font-awesome-icon icon="plus" class="me-1" /> New Event
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search by reference or title..." />
      <select v-model="filterStatus" @change="fetchEvents" class="filter-select">
        <option value="">All Statuses</option>
        <option value="draft">Draft</option>
        <option value="open">Open</option>
        <option value="closed">Closed</option>
        <option value="evaluation">Evaluation</option>
        <option value="award_recommended">Award Recommended</option>
        <option value="awarded">Awarded</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <select v-model="filterType" @change="fetchEvents" class="filter-select">
        <option value="">All Types</option>
        <option value="rfq">RFQ</option>
        <option value="rfp">RFP</option>
        <option value="rfi">RFI</option>
        <option value="eoi">EOI</option>
      </select>
      <input type="date" v-model="dateFrom" class="filter-input" style="max-width:160px" />
      <input type="date" v-model="dateTo" class="filter-input" style="max-width:160px" />
      <button class="btn-apply" @click="applyDateFilter">Apply</button>
      <button class="btn-clear" @click="clearDateFilter" v-if="dateFrom || dateTo">Clear</button>
    </div>

    <!-- Table -->
    <div class="card">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div><p>Loading...</p>
      </div>

      <div v-else-if="!events.length" class="empty-state">
        <font-awesome-icon icon="file-contract" class="empty-icon" />
        <p>No sourcing events found</p>
        <span>Create a sourcing event to invite vendors and collect quotes</span>
      </div>

      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Reference</th>
            <th>Title</th>
            <th>Type</th>
            <th>Linked PR</th>
            <th>Deadline</th>
            <th>Vendors</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ev in events" :key="ev.id" class="row-link" @click="goToDetail(ev.id)">
            <td><span class="mono">{{ ev.event_number }}</span></td>
            <td><div class="ev-title">{{ ev.title }}</div></td>
            <td><span :class="['type-badge', `type-badge--${ev.event_type}`]">{{ ev.event_type.toUpperCase() }}</span></td>
            <td>
              <span v-if="ev.pr" class="mono-sm">{{ ev.pr.pr_number }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td>
              <span v-if="ev.submission_deadline" :class="isOverdue(ev.submission_deadline) && ev.status === 'open' ? 'date-overdue' : ''">
                {{ formatDate(ev.submission_deadline) }}
              </span>
              <span v-else class="text-muted">—</span>
            </td>
            <td>
              <span class="vendor-count">{{ ev.event_vendors_count }}</span>
            </td>
            <td><span :class="['status-badge', `status-badge--${ev.status}`]">{{ formatStatus(ev.status) }}</span></td>
            <td @click.stop>
              <div class="action-menu">
                <button class="btn-icon" @click="goToDetail(ev.id)" title="View"><font-awesome-icon icon="eye" /></button>
                <button v-if="ev.status === 'draft'" class="btn-icon" @click="$router.push(`/procurement/sourcing-events/${ev.id}/edit`)" title="Edit"><font-awesome-icon icon="edit" /></button>
                <button v-if="ev.status === 'draft'" class="btn-icon" @click="issueEvent(ev)" title="Issue to vendors"><font-awesome-icon icon="paper-plane" /></button>
                <button v-if="['draft','cancelled'].includes(ev.status)" class="btn-icon btn-icon--danger" @click="confirmDelete(ev)" title="Delete"><font-awesome-icon icon="trash" /></button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination" v-if="meta && meta.last_page > 1">
        <button :disabled="meta.current_page === 1" @click="changePage(meta.current_page - 1)" class="page-btn">&#8592;</button>
        <span class="page-info">Page {{ meta.current_page }} of {{ meta.last_page }}</span>
        <button :disabled="meta.current_page === meta.last_page" @click="changePage(meta.current_page + 1)" class="page-btn">&#8594;</button>
      </div>
    </div>

    <!-- Delete Confirm -->
    <div v-if="deleteTarget" class="modal-backdrop" @click.self="deleteTarget = null">
      <div class="modal-box">
        <div class="modal-header">
          <h6>Delete Sourcing Event</h6>
          <button class="btn-close-modal" @click="deleteTarget = null">&times;</button>
        </div>
        <div class="modal-body">
          <p>Delete <strong>{{ deleteTarget.event_number }}</strong> — <em>{{ deleteTarget.title }}</em>? This cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" :disabled="deleting" @click="deleteEvent">{{ deleting ? 'Deleting...' : 'Delete' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'SourcingEvents',
  setup() { return { toast: useToast() }; },
  data() {
    return {
      events: [], meta: null, loading: false,
      search: '', filterStatus: '', filterType: '', page: 1,
      dateFrom: '', dateTo: '', exportMenuOpen: false, exporting: false,
      deleteTarget: null, deleting: false,
      searchTimer: null,
    };
  },
  mounted() {
    this.fetchEvents();
    document.addEventListener('click', this.onOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onOutsideClick);
  },
  methods: {
    async fetchEvents() {
      this.loading = true;
      try {
        const { data } = await axios.get('/procurement/sourcing-events', {
          params: {
            search: this.search, status: this.filterStatus, event_type: this.filterType, page: this.page,
            from: this.dateFrom || undefined, to: this.dateTo || undefined,
          },
        });
        this.events = data.data.data;
        this.meta   = data.data;
      } catch { this.toast.error('Failed to load sourcing events'); }
      finally { this.loading = false; }
    },
    debouncedFetch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.page = 1; this.fetchEvents(); }, 350);
    },
    changePage(p) { this.page = p; this.fetchEvents(); },
    applyDateFilter() { this.page = 1; this.fetchEvents(); },
    clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; this.page = 1; this.fetchEvents(); },
    async doExport(format) {
      this.exportMenuOpen = false;
      this.exporting = true;
      try {
        const params = { format };
        if (this.search)       params.search     = this.search;
        if (this.filterStatus) params.status     = this.filterStatus;
        if (this.filterType)   params.event_type = this.filterType;
        if (this.dateFrom)     params.from       = this.dateFrom;
        if (this.dateTo)       params.to         = this.dateTo;
        const { data } = await axios.get('/procurement/sourcing-events/export', { params, responseType: 'blob' });
        const mime = format === 'pdf' ? 'application/pdf' : 'text/csv';
        const ext  = format === 'pdf' ? 'pdf' : 'csv';
        const ts   = new Date().toISOString().slice(0, 10);
        const url  = URL.createObjectURL(new Blob([data], { type: mime }));
        const a    = document.createElement('a');
        a.href = url; a.download = `sourcing_events_${ts}.${ext}`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch { this.toast?.error('Export failed. Please try again.'); }
      finally  { this.exporting = false; }
    },
    onOutsideClick(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
    },
    goToDetail(id) { this.$router.push(`/procurement/sourcing-events/${id}`); },
    async issueEvent(ev) {
      if (!confirm(`Issue ${ev.event_number} and send invitations to ${ev.event_vendors_count} invited vendor(s)?`)) return;
      try {
        await axios.post(`/procurement/sourcing-events/${ev.id}/issue`);
        this.toast.success('Event issued — vendor invitations sent');
        this.fetchEvents();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to issue event');
      }
    },
    confirmDelete(ev) { this.deleteTarget = ev; },
    async deleteEvent() {
      this.deleting = true;
      try {
        await axios.delete(`/procurement/sourcing-events/${this.deleteTarget.id}`);
        this.toast.success('Event deleted');
        this.deleteTarget = null;
        this.fetchEvents();
      } catch { this.toast.error('Failed to delete event'); }
      finally { this.deleting = false; }
    },
    formatStatus(s) {
      const map = {
        draft:'Draft', open:'Open', closed:'Closed',
        evaluation:'Evaluation', award_recommended:'Award Recommended',
        awarded:'Awarded', cancelled:'Cancelled',
      };
      return map[s] ?? s;
    },
    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
    },
    isOverdue(d) { return d && new Date(d) < new Date(); },
  },
};
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.me-1        { margin-right:4px; }

.filter-bar   { display:flex; gap:12px; margin-bottom:16px; flex-wrap:wrap; }
.filter-input { flex:1; min-width:200px; padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; outline:none; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; background:#fff; cursor:pointer; outline:none; }

.card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.loading-state,.empty-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; color:#7a90b0; gap:10px; }
.empty-icon { font-size:40px; color:#dde5f0; }
.empty-state p { font-size:15px; font-weight:600; color:#1a2b45; margin:0; }

.data-table { width:100%; border-collapse:collapse; }
.data-table th { padding:11px 16px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; white-space:nowrap; }
.data-table td { padding:13px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.row-link { cursor:pointer; transition:background .12s; }
.row-link:hover { background:#f8fafc; }
.row-link:last-child td { border-bottom:none; }

.ev-title    { font-weight:600; }
.mono        { font-family:monospace; font-size:12px; color:#004AAD; font-weight:600; }
.mono-sm     { font-family:monospace; font-size:11.5px; color:#004AAD; }
.text-muted  { color:#aab8cc; font-size:12px; }
.date-overdue { color:#dc2626; font-weight:600; }
.vendor-count { display:inline-block; min-width:24px; text-align:center; background:#f1f5f9; border-radius:20px; padding:2px 8px; font-size:12px; font-weight:700; color:#475569; }

.type-badge { display:inline-block; font-size:11px; font-weight:700; border-radius:4px; padding:3px 8px; letter-spacing:.05em; }
.type-badge--rfq { background:#eff6ff; color:#1d4ed8; }
.type-badge--rfp { background:#f5f3ff; color:#6d28d9; }
.type-badge--rfi { background:#fff7ed; color:#c2410c; }
.type-badge--eoi { background:#f0fdf4; color:#15803d; }

.status-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; white-space:nowrap; }
.status-badge--draft             { background:#f1f5f9; color:#64748b; }
.status-badge--open              { background:#dcfce7; color:#15803d; }
.status-badge--closed            { background:#fef9c3; color:#854d0e; }
.status-badge--evaluation        { background:#e0f2fe; color:#0369a1; }
.status-badge--award_recommended { background:#fff7ed; color:#c2410c; }
.status-badge--awarded           { background:#d1fae5; color:#065f46; }
.status-badge--cancelled         { background:#fee2e2; color:#dc2626; }

.action-menu { display:flex; gap:6px; }
.btn-icon { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; color:#1a2b45; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:all .15s; }
.btn-icon:hover { background:#f4f7fb; }
.btn-icon--danger:hover { background:#fee2e2; border-color:#fecaca; color:#dc2626; }

.pagination { display:flex; align-items:center; justify-content:center; gap:12px; padding:16px; border-top:1px solid #f0f4f8; }
.page-btn   { padding:7px 14px; border:1px solid #dde5f0; border-radius:7px; background:#fff; cursor:pointer; font-size:13px; }
.page-btn:disabled { opacity:.4; cursor:not-allowed; }
.page-btn:hover:not(:disabled) { background:#f4f7fb; }
.page-info  { font-size:13px; color:#7a90b0; }

.modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
.modal-box  { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #e4eaf2; }
.modal-header h6 { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.btn-close-modal { background:transparent; border:none; font-size:22px; color:#7a90b0; cursor:pointer; }
.modal-body { padding:20px 24px; font-size:13.5px; color:#1a2b45; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #e4eaf2; }

.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover { background:#003b8e; }
.btn-outline { display:inline-flex; align-items:center; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }
.btn-danger  { display:inline-flex; align-items:center; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; }
.btn-danger:hover:not(:disabled) { background:#b91c1c; }
.btn-danger:disabled { opacity:.5; cursor:not-allowed; }

.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

@media(max-width:768px) {
  .data-table th:nth-child(4), .data-table td:nth-child(4),
  .data-table th:nth-child(5), .data-table td:nth-child(5) { display:none; }
}
.header-actions{display:flex;align-items:center;gap:10px}
.export-wrap{position:relative}
.btn-export{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.btn-export:hover{background:#e8edf5}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;top:calc(100% + 6px);right:0;background:#fff;border:1.5px solid #e4eaf2;border-radius:10px;box-shadow:0 6px 20px rgba(0,0,0,.1);z-index:200;min-width:160px;overflow:hidden}
.export-opt{display:flex;align-items:center;width:100%;padding:10px 14px;background:none;border:none;font-size:13px;color:#1a2b45;cursor:pointer;text-align:left}
.export-opt:hover{background:#f4f7fb}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-apply:hover{background:#003b8e}
.btn-clear{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-clear:hover{background:#e8edf5}
</style>
